/**
 * Sidebar Dinámico - Construye el menú desde la base de datos
 * Lee la estructura de módulos según los permisos del usuario
 */

import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Toolbar,
  Box,
  Typography,
  useTheme,
  alpha,
  Collapse,
  CircularProgress
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  Dashboard as DashboardIcon,
  Description,
  Apartment,
  Person,
  Settings,
  People,
  Badge,
  Business,
  ManageAccounts,
  Payment,
  Memory,
  History,
  Speed as SpeedIcon,
  Backup as BackupIcon,
  EmojiEvents as ObjetivosIcon,
  Work as GestionIcon,
  Autorenew as RenovacionesIcon,
  ViewList as VistaIndividualIcon,
  ViewModule as VistaAgrupadaIcon,
  Notifications as NotificationsIcon,
  ContactMail as OportunidadesIcon,
  Leaderboard,
  TrendingDown,
  Receipt,
  Store,
  Calculate as CalculateIcon,
  Article as LogsIcon,
  BugReport,
  Security
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { getUserMenu } from "../services/modulesService";
import { getCurrentVersion } from "../services/versionService";

// Mapeo de iconos según el nombre del ícono en la base de datos
const iconMap = {
  DashboardIcon: <DashboardIcon />,
  Description: <Description />,
  Apartment: <Apartment />,
  Person: <Person />,
  Settings: <Settings />,
  People: <People />,
  Badge: <Badge />,
  Business: <Business />,
  ManageAccounts: <ManageAccounts />,
  Payment: <Payment />,
  Memory: <Memory />,
  History: <History />,
  Speed: <SpeedIcon />,
  Backup: <BackupIcon />,
  EmojiEvents: <ObjetivosIcon />,
  Work: <GestionIcon />,
  Autorenew: <RenovacionesIcon />,
  ViewList: <VistaIndividualIcon />,
  ViewModule: <VistaAgrupadaIcon />,
  Notifications: <NotificationsIcon />,
  ContactMail: <OportunidadesIcon />,
  Leaderboard: <Leaderboard />,
  TrendingDown: <TrendingDown />,
  Receipt: <Receipt />,
  Store: <Store />,
  Calculate: <CalculateIcon />,
  Article: <LogsIcon />,
  BugReport: <BugReport />,
  Security: <Security />
};

const SidebarDynamic = ({ open, toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [menuModules, setMenuModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const [appVersion, setAppVersion] = useState("1.0.0");

  // Cargar módulos del menú
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setLoading(true);
        const response = await getUserMenu();
        setMenuModules(response.data);

        // Función recursiva para verificar si un módulo o sus descendientes están activos
        const hasActiveDescendant = (module) => {
          // Si el módulo tiene ruta y está activo
          if (module.route && (location.pathname === module.route || location.pathname.startsWith(module.route + '/'))) {
            return true;
          }
          // Si tiene hijos, verificar recursivamente
          if (module.children && module.children.length > 0) {
            return module.children.some(child => hasActiveDescendant(child));
          }
          return false;
        };

        // Abrir todas las secciones que contienen la ruta activa
        const initialOpenState = {};
        const expandActiveParents = (modules) => {
          modules.forEach(module => {
            if (module.children && module.children.length > 0) {
              if (hasActiveDescendant(module)) {
                initialOpenState[module.id] = true;
              }
              expandActiveParents(module.children);
            }
          });
        };

        expandActiveParents(response.data);
        setOpenSections(initialOpenState);
      } catch (err) {
        console.error('Error cargando menú:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchMenu();
    }
  }, [open, location.pathname]);

  // Cargar versión de la aplicación
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const version = await getCurrentVersion();
        setAppVersion(version.versionString || "1.0.0");
      } catch (error) {
        console.error("Error al cargar versión:", error);
        // Mantener versión por defecto
      }
    };

    fetchVersion();
  }, []);

  const handleNavigate = (path) => {
    if (path) {
      navigate(path);
      toggleSidebar();
    }
  };

  const isActive = (path) => {
    if (!path) return false;

    // Coincidencia exacta
    if (location.pathname === path) return true;

    // Para rutas hijas (ej: /directores/123 debe marcar /directores como activo)
    // PERO /directores/agrupados NO debe marcar /directores como activo
    // Verificamos que después del path no venga una palabra (sino un ID u otro segmento dinámico)
    if (location.pathname.startsWith(path + '/')) {
      const remainingPath = location.pathname.substring(path.length + 1);
      // Si el resto es solo segmentos que parecen IDs o GUIDs (no palabras conocidas), marcar activo
      // Si el resto comienza con una palabra conocida (como "agrupados"), NO marcar activo
      const knownRoutes = ['agrupados', 'editar', 'solicitud', 'fianzas'];
      const firstSegment = remainingPath.split('/')[0];
      return !knownRoutes.includes(firstSegment);
    }

    return false;
  };

  const toggleSection = (moduleId) => {
    setOpenSections(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const getIcon = (iconName) => {
    return iconMap[iconName] || <Description />;
  };

  // Renderizar un item del menú (sin hijos)
  const renderMenuItem = (module, level = 0) => (
    <ListItem key={module.id} disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        onClick={() => handleNavigate(module.route)}
        selected={isActive(module.route)}
        sx={{
          pl: 2 + (level * 2),
          borderRadius: 2,
          "&.Mui-selected": {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.18),
            },
            "& .MuiListItemIcon-root": {
              color: theme.palette.primary.main,
            },
          },
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 40,
            color: isActive(module.route) ? theme.palette.primary.main : theme.palette.text.secondary,
          }}
        >
          {getIcon(module.icon)}
        </ListItemIcon>
        <ListItemText
          primary={module.displayName}
          primaryTypographyProps={{
            fontWeight: isActive(module.route) ? 600 : 500,
            fontSize: "0.9rem",
          }}
        />
      </ListItemButton>
    </ListItem>
  );

  // Renderizar sección con hijos (collapsible)
  const renderSection = (module, level = 0) => {
    const hasChildren = module.children && module.children.length > 0;
    const isOpen = openSections[module.id] || false;

    if (!hasChildren) {
      return renderMenuItem(module, level);
    }

    return (
      <React.Fragment key={module.id}>
        <ListItem disablePadding sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => toggleSection(module.id)}
            sx={{
              pl: 2 + (level * 2),
              borderRadius: 2,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: theme.palette.text.secondary }}>
              {getIcon(module.icon)}
            </ListItemIcon>
            <ListItemText
              primary={module.displayName}
              primaryTypographyProps={{
                fontWeight: 500,
                fontSize: "0.9rem",
              }}
            />
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>

        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {module.children.map(child => {
              // Si el child también tiene hijos, renderizar como sección anidada
              const childHasChildren = child.children && child.children.length > 0;
              if (childHasChildren) {
                return renderSection(child, level + 1);
              }
              return renderMenuItem(child, level + 1);
            })}
          </List>
        </Collapse>
      </React.Fragment>
    );
  };

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={toggleSidebar}
      sx={{
        "& .MuiDrawer-paper": {
          width: 280,
          borderRight: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column'
        },
      }}
    >
      {/* Sidebar Header - Mismo alto que AppBar */}
      <Toolbar
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          color: "white",
          minHeight: 64 // Mismo alto que AppBar por defecto
        }}
      >
        {/* Vacío - solo para mantener consistencia visual con AppBar */}
      </Toolbar>

      <Divider />

      {/* Menu Items */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Box sx={{ px: 3, py: 2 }}>
          <Typography color="error" variant="body2">
            Error cargando menú
          </Typography>
        </Box>
      ) : (
        <List sx={{ px: 2, py: 2, flexGrow: 1 }}>
          {menuModules.map((module, index) => (
            <React.Fragment key={module.id}>
              {renderSection(module, 0)}
              {index === 0 && <Divider sx={{ my: 2 }} />}
            </React.Fragment>
          ))}
        </List>
      )}

      {/* Sidebar Footer */}
      <Box
        sx={{
          mt: 'auto',
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          color: "white",
          borderTop: `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
          Versión {appVersion}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.7 }}>
          © 2025 Garantías App
        </Typography>
      </Box>
    </Drawer>
  );
};

export default SidebarDynamic;
